/**
 * Bulk Actions Toolbar Component
 *
 * Sticky toolbar that appears when transactions are selected.
 * Provides bulk approve and re-classify actions.
 *
 * Note: Bulk Reject has been removed. Transactions stay in Review Needed
 * until they are classified or matched via reconciliation. Hiding the
 * "reject" affordance avoids losing data the user may still need.
 */

import React from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { motion } from 'framer-motion';
import { 
  Check,
  RefreshCw,
  Undo2,
  Paperclip,
  Loader2
} from 'lucide-react';

export const BulkActionsToolbar = ({
  selectedCount = 0,
  totalCount = 0,
  isAllSelected = false,
  isProcessing = false,
  onToggleSelectAll,
  onClearSelection,
  onBulkApprove,
  onBulkReclassify,
  onBulkUndo, // Completed mode only: reverses classification back to Review Needed
  onBulkAttachDocs, // Feb 2026: attach one doc to every selected row (bulk_attach)
  onBulkMatch, // match the selected rows TOGETHER to one or many documents / recorded entries (match group)
}) => {
  if (selectedCount === 0) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border rounded-lg p-3 shadow-sm"
      data-testid="tx-bulk-actions-toolbar"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={isAllSelected}
            onCheckedChange={onToggleSelectAll}
            data-testid="tx-bulk-select-all"
          />
          <span className="font-medium text-sm">
            {selectedCount} of {totalCount} selected
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClearSelection}
            data-testid="tx-bulk-clear"
          >
            Clear
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          {onBulkApprove && (
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 gap-1"
              onClick={onBulkApprove}
              disabled={isProcessing}
              data-testid="tx-bulk-approve"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Bulk Approve
            </Button>
          )}
          {onBulkReclassify && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1 border-[#108981]/40 text-[#108981] hover:bg-[#108981]/10"
              onClick={onBulkReclassify}
              disabled={isProcessing}
              data-testid="tx-bulk-reclassify"
            >
              <RefreshCw className="w-4 h-4" />
              Re-classify
            </Button>
          )}
          {onBulkMatch && selectedCount >= 2 && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              onClick={onBulkMatch}
              disabled={isProcessing}
              data-testid="tx-bulk-match-selected"
              title="Match these bank lines together to one or several documents / entries already in the books"
            >
              <Paperclip className="w-4 h-4" />
              Match selected together
            </Button>
          )}
          {onBulkAttachDocs && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1 border-slate-300 text-slate-700 hover:bg-slate-50"
              onClick={onBulkAttachDocs}
              disabled={isProcessing}
              data-testid="tx-bulk-attach-docs"
            >
              <Paperclip className="w-4 h-4" />
              Attach Doc
            </Button>
          )}
          {onBulkUndo && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1 border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={onBulkUndo}
              disabled={isProcessing}
              data-testid="tx-bulk-undo"
            >
              <Undo2 className="w-4 h-4" />
              Send back to Review
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default BulkActionsToolbar;
